import React, { useEffect, useState } from 'react';
import { Loader2, RefreshCcw, ShieldAlert, Trash2 } from 'lucide-react';
import api from '../../api/client';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { useToast } from '../../hooks/use-toast';

const SECTIONS = [
  { key: 'posts', label: 'Post + media (TUTTI)' },
  { key: 'categories', label: 'Categorie: elimina ambigue → Generale · slug coppie → personal-trainer' },
  { key: 'avatars', label: 'Avatar non idonei (15 account)' },
  { key: 'bios', label: 'Bio ambigue (PRIMA → DOPO)' },
  { key: 'topics', label: 'Argomenti O2O (Relazioni → Comunicazione)' },
];
const errMsg = (e) => String(e.response?.data?.detail || '').split('|').pop() || 'Operazione non riuscita';

const Stat = ({ label, value, testId }) => (
  <div className="rounded-md border border-border bg-background/40 px-3 py-2" data-testid={testId}>
    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
    <p className="text-sm font-bold">{value}</p>
  </div>
);

const PlanDetails = ({ plan }) => (
  <div className="space-y-3 text-xs" data-testid="apple-cleanup-plan">
    <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
      <Stat label="Post da eliminare" value={plan.posts.total} testId="apple-plan-posts-total" />
      <Stat label="File media/poster" value={plan.posts.media_files} testId="apple-plan-media-files" />
      <Stat label="Oggetti Wasabi" value={plan.posts.wasabi_objects_to_delete} testId="apple-plan-wasabi" />
      <Stat label="Utenti con posts_count>0" value={plan.posts.users_with_posts_count} testId="apple-plan-users-posts" />
    </div>
    <p className="text-muted-foreground">Per tipo: {Object.entries(plan.posts.by_type).map(([k, v]) => `${k} ${v}`).join(' · ') || '—'} · like {plan.posts.likes} · commenti {plan.posts.comments} · salvati {plan.posts.saves}</p>
    <div>
      <p className="font-semibold">Categorie da eliminare → {plan.fallback_category}</p>
      {plan.categories.map((c) => (
        <p key={c.slug} data-testid={`apple-plan-cat-${c.slug}`}>
          /{c.slug} — {c.found ? `"${c.name}" · ${c.users} utenti · ${c.users_secondary} secondarie · ${c.posts} racconti · ${c.lives} live` : 'non presente'}
        </p>
      ))}
    </div>
    <div>
      <p className="font-semibold">Categorie da rinominare (slug)</p>
      {(plan.category_renames || []).map((c) => (
        <p key={c.from} data-testid={`apple-plan-cat-rename-${c.from}`}>
          /{c.from} → /{c.to} — {!c.found ? 'non presente' : c.target_exists ? `"${c.name}" · CONFLITTO: /${c.to} esiste già (verrà saltata)` : `"${c.name}" · ${c.users} utenti · ${c.users_secondary} secondarie · ${c.posts} racconti · ${c.lives} live`}
        </p>
      ))}
    </div>
    <div>
      <p className="font-semibold">Avatar da rimuovere ({plan.avatars.length})</p>
      {plan.avatars.map((a) => (
        <p key={a.username} data-testid={`apple-plan-avatar-${a.username}`}>
          @{a.username}{a.kind === 'prudential' ? ' [prudenziale]' : ''} — {!a.found ? 'utente non trovato' : a.has_avatar ? 'avatar presente' : 'nessun avatar'}
        </p>
      ))}
    </div>
    <div>
      <p className="font-semibold">Bio da modificare</p>
      {plan.bios.map((b) => (
        <div key={b.username} className="mb-1" data-testid={`apple-plan-bio-${b.username}`}>
          <p>@{b.username} [{b.prudential ? 'prudenziale' : 'certo'}] — {!b.found ? 'utente non trovato' : b.matches_audit ? 'bio identica all’audit ✔' : 'bio CAMBIATA rispetto all’audit (verrà saltata)'}</p>
          <p className="text-muted-foreground">DOPO: {b.after}</p>
        </div>
      ))}
    </div>
    <p className="font-semibold">Argomenti O2O</p>
    <p className="text-muted-foreground">Attuali: {plan.topics.topic_options.join(', ') || '—'} · utenti con argomento da rinominare: {plan.topics.users_affected}</p>
    {plan.last_run && (
      <p className="text-amber-500" data-testid="apple-plan-last-run">Ultima esecuzione: {plan.last_run.started_at} · sezioni {plan.last_run.sections.join(', ')}</p>
    )}
  </div>
);

const AdminAppleCleanupPanel = () => {
  const { toast } = useToast();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(SECTIONS.map((s) => s.key));
  const [prudential, setPrudential] = useState(true);
  const [confirm, setConfirm] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setPlan((await api.get('/admin/apple-cleanup/plan')).data); } catch (e) { toast({ title: 'Errore', description: errMsg(e), variant: 'destructive' }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const run = async () => {
    if (confirm !== 'ELIMINA' || running || !selected.length) return;
    setRunning(true);
    try {
      const r = await api.post('/admin/apple-cleanup/run', { sections: selected, confirm, include_prudential: prudential });
      setResult(r.data);
      setConfirm('');
      toast({ title: 'Pulizia eseguita', description: `Sezioni: ${r.data.sections.join(', ')}` });
      load();
    } catch (e) {
      toast({ title: 'Errore', description: errMsg(e), variant: 'destructive' });
    } finally { setRunning(false); }
  };

  return (
    <div className="max-w-3xl space-y-4" data-testid="admin-apple-cleanup-panel">
      <div className="flex items-start gap-2">
        <ShieldAlert className="mt-0.5 h-5 w-5 text-amber-500" />
        <div>
          <h2 className="text-lg font-bold">Pulizia Apple 1.1.4</h2>
          <p className="text-xs text-muted-foreground">
            Strumento una tantum. <b>Dry-run</b> sotto: nessuna modifica finché non scrivi ELIMINA ed esegui. Le operazioni sono definitive e idempotenti.
          </p>
        </div>
        <Button size="sm" variant="outline" className="ml-auto" onClick={load} disabled={loading} data-testid="apple-cleanup-refresh-btn">
          <RefreshCcw className="h-4 w-4" />
        </Button>
      </div>

      {loading ? <div className="flex justify-center p-4"><Loader2 className="h-4 w-4 animate-spin" /></div> : plan && <PlanDetails plan={plan} />}

      <div className="space-y-2 rounded-md border border-destructive/40 bg-destructive/5 p-3">
        <p className="text-xs font-semibold">Sezioni da eseguire</p>
        {SECTIONS.map((s) => (
          <label key={s.key} className="flex items-center gap-2 text-xs" data-testid={`apple-cleanup-section-${s.key}`}>
            <Checkbox checked={selected.includes(s.key)} onCheckedChange={(v) => setSelected((cur) => (v ? [...cur, s.key] : cur.filter((k) => k !== s.key)))} />
            {s.label}
          </label>
        ))}
        <label className="flex items-center gap-2 text-xs" data-testid="apple-cleanup-prudential">
          <Checkbox checked={prudential} onCheckedChange={(v) => setPrudential(!!v)} />
          Includi anche le bio “prudenziali” (mimmo, luce, admin_904fac)
        </label>
        <div className="flex gap-2 pt-1">
          <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Scrivi ELIMINA" className="h-8 text-sm" data-testid="apple-cleanup-confirm-input" />
          <Button size="sm" variant="destructive" onClick={run} disabled={confirm !== 'ELIMINA' || running || !selected.length} data-testid="apple-cleanup-run-btn">
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Esegui
          </Button>
        </div>
      </div>

      {result && (
        <pre className="max-h-96 overflow-auto rounded-md border border-border bg-background/60 p-3 text-[11px]" data-testid="apple-cleanup-result">
          {JSON.stringify(result, null, 2)}
        </pre>
      )}
    </div>
  );
};

export default AdminAppleCleanupPanel;
